import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Plus } from "lucide-react";
import { SERVICES } from "@/data/site";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero, CTABanner } from "@/components/sections";
import { BookButton, CallButton } from "@/components/CTAButtons";

export default function ServicePage({ slug }) {
  const s = SERVICES[slug];
  return (
    <>
      <SEO title={s.seoTitle} description={s.seoDesc} />
      <PageHero
        kicker={s.kicker}
        titleLines={[s.headline]}
        intro={s.intro}
        image={s.image}
        imageAlt={s.imageAlt}
        category={s.slug}
      />

      {/* MENU / PRICING */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-28" data-testid="service-menu">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="label mb-8 text-wine">The menu — transparent pricing</p>
              <div className="border-t border-border">
                {s.menu.map((m, i) => (
                  <div key={i} className="grid grid-cols-12 items-baseline gap-4 border-b border-border py-6" data-testid={`menu-item-${slug}-${i}`}>
                    <div className="col-span-8 md:col-span-9">
                      <h3 className="font-display text-xl md:text-2xl">{m.name}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{m.desc}</p>
                    </div>
                    <div className="col-span-4 text-right md:col-span-3">
                      <span className="font-display text-xl md:text-2xl">{m.price}</span>
                      <p className="mt-1 font-mono text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground">{m.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12">
                <p className="label mb-6">Popular add-ons</p>
                <ul className="flex flex-wrap gap-3">
                  {s.addOns.map((a, i) => (
                    <li key={i} className="flex items-center gap-2 border border-border px-4 py-2 text-sm text-foreground/90">
                      <Plus className="h-3.5 w-3.5 text-wine" strokeWidth={1.5} /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-5 lg:border-l lg:border-border lg:pl-12">
            <Reveal>
              <div className="border border-border p-8">
                <p className="label mb-4 text-wine">Book this studio</p>
                <p className="font-display text-2xl leading-snug">Ready for {s.name.toLowerCase()}?</p>
                <p className="mt-3 text-sm text-muted-foreground">Reserve online, or call and we'll match you with the right specialist.</p>
                <div className="mt-6 flex flex-col gap-3">
                  <BookButton category={s.slug} className="w-full justify-center" testid={`service-book-${slug}`} />
                  <CallButton label="Call / Text for inquiries" testid={`service-call-${slug}`} className="w-full justify-center" />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8">
                <p className="label mb-5">Products & brands</p>
                <ul className="flex flex-wrap gap-2">
                  {s.brands.map((b) => (
                    <li key={b} className="border border-border px-3 py-1.5 font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted-foreground">{b}</li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10">
                <p className="label mb-5">Why our specialists</p>
                <ul className="space-y-4">
                  {s.why.map((w, i) => (
                    <li key={i} className="flex gap-3 text-sm text-foreground/90">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-wine" strokeWidth={2} /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10">
                <p className="label mb-5">Pairs well with</p>
                <div className="space-y-3">
                  {s.related.map((r) => (
                    <Link key={r.path} to={r.path} className="flex items-center justify-between border-b border-border py-3 text-sm text-foreground/90 hover:text-foreground">
                      {r.label} <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <CTABanner category={s.slug} title={`Book your ${s.name.toLowerCase()} appointment.`} />
    </>
  );
}
